import type { LhcItemDetail, RelatedDeliveryOrder } from '@diamond/shared/types';
import { formatCurrencyIdr, unixToHumanDate } from '@diamond/shared/utils';

import { COLLECTION_QUERY_KEYS } from '../api/constants';
import type { LHCDetailInvoiceRowData } from '../types';

export function getRelatedDeliveryOrdersQueryKey(
  lhcId: string,
  orderMetaId: string | null,
  poNumber: string
) {
  return [
    COLLECTION_QUERY_KEYS.RELATED_DELIVERY_ORDERS,
    lhcId,
    orderMetaId ?? poNumber,
  ] as const;
}

export function canFetchRelatedDeliveryOrders(
  item: Pick<LhcItemDetail, 'delivery_order_id' | 'po_number'>
): boolean {
  return !!(
    item.delivery_order_id &&
    item.po_number &&
    item.po_number !== '-'
  );
}

export function resolveRelatedDeliveryOrderCount(
  item: Pick<LhcItemDetail, 'related_delivery_order_count'>
): number {
  return item.related_delivery_order_count;
}

function createRelatedLoadingPlaceholderRow(
  parent: LHCDetailInvoiceRowData
): LHCDetailInvoiceRowData {
  return {
    rowId: `related-loading-${parent.rowId}`,
    parentId: parent.rowId,
    orderMetaId: parent.orderMetaId,
    no: '',
    poNumber: '-',
    plant: '-',
    invoiceDate: '-',
    deliveryOrderNo: 'Memuat...',
    invoiceNo: '-',
    billTo: '-',
    customerName: '-',
    outletName: '-',
    poValue: '-',
    soValue: '-',
    invoiceValue: '-',
    differenceAmount: '-',
    mutationValue: '-',
    bankMutationNo: '-',
    depositUsed: '-',
    hasPaymentProof: false,
    paymentProofIds: [],
    bankMutations: [],
    bank: '-',
    remark: '-',
    isChildRow: true,
    hasChildren: false,
    isRelatedLoadingPlaceholder: true,
  };
}

export function mapRelatedDeliveryOrderToChildRow(
  order: RelatedDeliveryOrder,
  parent: LHCDetailInvoiceRowData
): LHCDetailInvoiceRowData {
  return {
    rowId: `related-${order.delivery_order_id}`,
    parentId: parent.rowId,
    orderMetaId: parent.orderMetaId,
    deliveryOrderId: order.delivery_order_id,
    no: '',
    poNumber: order.linked_lhc_no || '-',
    plant: order.plant_name || '-',
    invoiceDate: order.delivery_date
      ? unixToHumanDate(order.delivery_date, 'd MMM yy')
      : '-',
    deliveryOrderNo: order.delivery_order_number || '-',
    invoiceNo: order.invoice_no || '-',
    billTo: '',
    customerName: order.customer_name || '-',
    outletName: order.outlet_name || '-',
    poValue: '',
    soValue: '',
    invoiceValue: formatCurrencyIdr(order.invoice_amount),
    differenceAmount: '',
    mutationValue: '',
    bankMutationNo: '',
    depositUsed: '',
    hasPaymentProof: false,
    paymentProofIds: [],
    bankMutations: [],
    bank: '',
    remark: '',
    isChildRow: true,
    hasChildren: false,
    canAddToLhc: order.can_add_to_lhc ?? false,
    linkedLhcNo: order.linked_lhc_no ?? null,
  };
}

export function buildLhcDetailRowsWithRelated(
  parentRows: LHCDetailInvoiceRowData[],
  relatedByCacheKey: Map<string, RelatedDeliveryOrder[]>,
  expandedRowIds: Set<string>,
  loadingRelatedCacheKeys: Set<string> = new Set()
): LHCDetailInvoiceRowData[] {
  return parentRows.flatMap((parent) => {
    const parentRow = {
      ...parent,
      isExpanded: parent.hasChildren && expandedRowIds.has(parent.rowId),
    };

    if (!parent.hasChildren || !expandedRowIds.has(parent.rowId)) {
      return [parentRow];
    }

    const cacheKey = parent.orderMetaId ?? parent.poNumber;
    if (!cacheKey || cacheKey === '-') {
      return [parentRow];
    }
    const relatedItems = relatedByCacheKey.get(cacheKey) ?? [];
    if (relatedItems.length === 0 && loadingRelatedCacheKeys.has(cacheKey)) {
      return [parentRow, createRelatedLoadingPlaceholderRow(parent)];
    }

    return [
      parentRow,
      ...relatedItems.map((item) =>
        mapRelatedDeliveryOrderToChildRow(item, parent)
      ),
    ];
  });
}
